import React, { useState, useEffect } from 'react';
import { pazientiAPI, mediciAPI } from '../../services/api';
import { templateAPI } from '../../services/templateApi';
import FirmaCanvas from './FirmaCanvas';
import { useFirmaAvanzata, firmaConsensoConTavoletta } from '../../services/firmaAvanzata';
import { messaggioDa } from '../../utils/notifiche';
import '../../styles/ModuloAnagrafico.css';
import '../../styles/ModulisticaWizard.css';
import '../../styles/FirmaConsenso.css';
import '../../styles/ModuloAnagraficoConsole.css';
import '../../styles/ModulisticaWizardConsole.css';
import '../../styles/FirmaConsensoConsole.css';

/**
 * FirmaConsenso
 * Compilazione e firma dei consensi richiesti dalle prestazioni di una
 * procedura. Se i consensi sono piu' di uno vengono proposti in sequenza.
 *
 * Props:
 *  - pazienteId
 *  - visitaId
 *  - medicoId
 *  - consensi    array di modelli (con eventuale campo _prestazione)
 *  - obbligatori true quando si firma PRIMA di creare la procedura: senza
 *                questi consensi la prestazione non si registra (blocco
 *                del 22 settembre 2026), quindi niente «Firma più tardi»
 *  - onClose()
 *  - onComplete(documenti)
 *
 * DUE FIRME, UN SOLO COMPONENTE. Con la firma interna (di serie, e come
 * sempre) il paziente disegna sul riquadro. Se il server dice che la firma
 * avanzata e' accesa (FIRMA_CONSENSI_PROVIDER=namirial, services/firmaAvanzata.js) il
 * riquadro lascia il posto alla tavoletta: il PDF lo prepara il server, lo
 * firma il servizio locale FirmaCerta, il server archivia il firmato. La
 * schermata prima di saperlo non si disegna (si aspetta nel «Caricamento»),
 * cosi' non cambia sotto le dita di chi ha gia' cominciato a firmare.
 * Un modello con anche la firma del medico non si firma con la tavoletta:
 * il PDF della FEA ha il solo riquadro del paziente.
 */

/* ---------- Sostituzione segnaposto (anteprima a video) ---------- */

const dataIta = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d) ? '' : d.toLocaleDateString('it-IT');
};

/* `struttura` arriva da fuori e non e' piu' scritto qui.

   Era `'Rimedical S.r.l.'`, cablato. Finiva nel segnaposto {{struttura}}
   DENTRO IL TESTO DI UN CONSENSO CHE IL PAZIENTE FIRMA: su
   un'installazione venduta, quel paziente avrebbe firmato un documento
   che nomina un'altra clinica. Il nome lo dice il server, che lo legge
   dai parametri della struttura. */
const costruisciMappa = (paziente, medico, prestazione, struttura) => ({
    'paziente.nome': paziente?.nome || '',
    'paziente.cognome': paziente?.cognome || '',
    'paziente.nome_cognome': [paziente?.cognome, paziente?.nome].filter(Boolean).join(' '),
    'paziente.codice_fiscale': paziente?.codice_fiscale || '',
    'paziente.data_nascita': dataIta(paziente?.data_nascita),
    'paziente.luogo_nascita': paziente?.luogo_nascita || '',
    'paziente.id': paziente?.id_paziente || '',
    'medico.nome': medico?.nome || '',
    'medico.cognome': medico?.cognome || '',
    'medico.nome_cognome': [medico?.cognome, medico?.nome].filter(Boolean).join(' '),
    'medico.specializzazione': medico?.specializzazione || '',
    'medico.numero_ordine': medico?.numero_ordine || '',
    'prestazione': prestazione || '',
    'data': new Date().toLocaleDateString('it-IT'),
    /* Il ripiego e' neutro, mai il nome di un'altra clinica: se il
       server non risponde si legge «la struttura», che si nota. */
    'struttura': struttura || 'la struttura',
});

const sostituisci = (testo, mappa) => {
    if (!testo) return '';
    return String(testo).replace(/\{\{\s*([\w.]+)\s*\}\}/g, (intero, chiave) => {
        const valore = mappa[chiave];
        return (valore === undefined || valore === '') ? intero : valore;
    });
};

/** Rende **grassetto** e _corsivo_ come elementi React. */
const conFormattazione = (testo) => {
    const pezzi = String(testo).split(/(\*\*[^*]+\*\*|_[^_]+_)/g).filter((p) => p !== '');
    return pezzi.map((pezzo, i) => {
        if (pezzo.startsWith('**') && pezzo.endsWith('**')) {
            return <strong key={i}>{pezzo.slice(2, -2)}</strong>;
        }
        if (pezzo.startsWith('_') && pezzo.endsWith('_')) {
            return <em key={i}>{pezzo.slice(1, -1)}</em>;
        }
        return <React.Fragment key={i}>{pezzo}</React.Fragment>;
    });
};

/* ---------- La tavoletta (firma avanzata) ---------- */

const MSG_FEA_CON_MEDICO = 'Questo modello chiede anche la firma del medico: con la tavoletta non si firma ancora.';

/* Una frase per passo, fissa: nel messaggio non entra mai un dato del paziente. */
const FRASI_TAVOLETTA = {
    attesa: 'Il paziente firma sulla tavoletta collegata a questo computer. Premi «Firma con la tavoletta» quando ha letto il consenso.',
    prepara: 'Preparo il documento…',
    tavoletta: 'Cerco la tavoletta…',
    documento: 'Il documento passa al servizio di firma…',
    firma: 'Il paziente firma ora sulla tavoletta.',
    lettura: 'Leggo il documento firmato…',
    archivia: 'Archivio il consenso firmato…',
};

/* ---------- Componente ---------- */

const FirmaConsenso = ({ pazienteId, visitaId, medicoId, consensi = [], obbligatori = false, onClose, onComplete }) => {
    const [indice, setIndice] = useState(0);
    const [paziente, setPaziente] = useState(null);
    const [medico, setMedico] = useState(null);
    const [risposte, setRisposte] = useState({});
    const [firmaPaziente, setFirmaPaziente] = useState(null);
    const [firmaMedico, setFirmaMedico] = useState(null);
    const [caricamento, setCaricamento] = useState(true);
    const [struttura, setStruttura] = useState('');
    const statoFirma = useFirmaAvanzata();
    const fea = !!(statoFirma && statoFirma.avanzata);
    const [passoTavoletta, setPassoTavoletta] = useState(null);

    /* Come si chiama questa clinica lo dice il server: qui non sta piu'
       scritto. La rotta non e' autenticata perche' la usa anche il tablet,
       dove nessun utente e' collegato. */
    useEffect(() => {
        fetch('/api/struttura')
            .then((r) => r.json())
            .then((d) => setStruttura(d?.data?.ragione_sociale || ''))
            .catch(() => {});
    }, []);

    const [invio, setInvio] = useState(false);
    const [errore, setErrore] = useState(null);
    const [archiviati, setArchiviati] = useState([]);

    const corrente = consensi[indice];
    const blocchi = corrente?.contenuto?.blocchi || [];
    const conMedico = corrente?.contenuto?.firme === 'paziente_medico';

    /* ---------- Caricamento anagrafiche ---------- */

    useEffect(() => {
        let attivo = true;

        const carica = async () => {
            try {
                const rp = await pazientiAPI.getById(pazienteId);
                if (attivo) setPaziente(rp.data.data.paziente);

                if (medicoId) {
                    const rm = await mediciAPI.getById(medicoId);
                    if (attivo) setMedico(rm.data?.data?.medico || rm.data?.data || null);
                }
            } catch (e) {
                console.error('Errore caricamento anagrafiche:', e);
                if (attivo) setErrore('Impossibile caricare i dati del paziente');
            } finally {
                if (attivo) setCaricamento(false);
            }
        };

        carica();
        return () => { attivo = false; };
    }, [pazienteId, medicoId]);

    /* ---------- Firma ---------- */

    const valida = () => {
        /* Con la tavoletta la firma non c'e' ancora: arriva dopo. Il resto e' uguale. */
        const mancanti = blocchi
            .map((b, i) => ({ b, i }))
            .filter(({ b, i }) => b.tipo === 'consenso' && risposte[i] === undefined);

        if (mancanti.length) return 'Esprimi una scelta su tutte le caselle di consenso';

        const negatiObbligatori = blocchi
            .map((b, i) => ({ b, i }))
            .filter(({ b, i }) => b.tipo === 'consenso' && b.obbligatorio && risposte[i] !== true);

        if (negatiObbligatori.length) {
            return 'Senza i consensi obbligatori la prestazione non puo essere eseguita';
        }

        if (fea) {
            if (conMedico) return MSG_FEA_CON_MEDICO;
            return null;
        }
        if (!firmaPaziente) return 'Manca la firma del paziente';
        if (conMedico && !firmaMedico) return 'Manca la firma del medico';

        return null;
    };

    const firma = async () => {
        const problema = valida();
        if (problema) {
            setErrore(problema);
            return;
        }

        try {
            setInvio(true);
            setErrore(null);

            let documento;
            if (fea) {
                /* L'ora e il browser li decide il server: qui non si mandano. */
                documento = await firmaConsensoConTavoletta({
                    template_id: corrente.id,
                    paziente_id: pazienteId,
                    visita_id: visitaId || null,
                    medico_id: medicoId || null,
                    prestazione: corrente._prestazione || null,
                    risposte,
                }, setPassoTavoletta);
            } else {
                const risposta = await templateAPI.firma({
                    template_id: corrente.id,
                    paziente_id: pazienteId,
                    visita_id: visitaId || null,
                    medico_id: medicoId || null,
                    prestazione: corrente._prestazione || null,
                    risposte,
                    firma_base64: firmaPaziente,
                    firma_medico_base64: conMedico ? firmaMedico : null,
                    firmato_il: new Date().toISOString(),
                    user_agent: navigator.userAgent
                });
                documento = risposta.data.data.documento;
            }

            const nuovi = [...archiviati, documento];
            setArchiviati(nuovi);

            if (indice < consensi.length - 1) {
                setIndice(indice + 1);
                setRisposte({});
                setFirmaPaziente(null);
                setFirmaMedico(null);
                document.querySelector('.modulo-body')?.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                if (onComplete) onComplete(nuovi);
                onClose();
            }
        } catch (e) {
            console.error('Errore firma consenso:', e);
            /* Con la tavoletta il motivo lo dice il server (il campo di firma mancante o
               gia' firmato, la «prepara» rifiutata) o il servizio: messaggioDa legge il
               primo, e lascia passare la frase di ErroreFirma. La firma a schermo resta
               com'era. */
            setErrore(fea ? messaggioDa(e, "Errore nell'archiviazione del consenso")
                : (e?.message || "Errore nell'archiviazione del consenso"));
        } finally {
            setInvio(false);
            setPassoTavoletta(null);
        }
    };

    const saltaTutto = () => {
        /* Obbligatori: chiudere vuol dire rinunciare a creare la procedura,
           non rimandare la firma. Quelli gia' firmati restano validi e il
           prossimo tentativo non li richiede. */
        if (obbligatori) {
            if (window.confirm(
                'Chiudere senza firmare?\n\n' +
                'La procedura non viene creata: senza questi consensi la prestazione non si registra.'
            )) onClose();
            return;
        }
        if (window.confirm(
            'Chiudere senza firmare i consensi rimanenti?\n\n' +
            'La procedura resta registrata, ma risulterà priva del consenso informato.'
        )) {
            if (onComplete && archiviati.length) onComplete(archiviati);
            onClose();
        }
    };

    /* ---------- Render ---------- */

    if (caricamento || statoFirma === null) {
        return (
            <div className="modulo-overlay">
                <div className="modulo-finestra fc-finestra">
                    <div className="fc-stato">Caricamento del consenso…</div>
                </div>
            </div>
        );
    }

    if (!corrente) {
        return null;
    }

    const mappa = costruisciMappa(paziente, medico, corrente._prestazione, struttura);

    return (
        <div className="modulo-overlay">
            <div className="modulo-finestra fc-finestra" role="dialog" aria-modal="true">

                <div className="modulo-body">
                    <header className="modulo-intestazione">
                        <div className="intestazione-ragione">RIMEDICAL S.r.l.</div>
                        <div className="intestazione-righe">
                            Via Europa 8 – 47822 Santarcangelo di Romagna (RN)<br />
                            Tel. 0541 620757 – Cell. 352 0453011<br />
                            P.IVA 04538930407 – Cod. Univoco KRRH6B9
                        </div>
                    </header>

                    {consensi.length > 1 && (
                        <div className="fc-avanzamento">
                            Consenso {indice + 1} di {consensi.length}
                        </div>
                    )}

                    <h2 className="modulo-titolo">{sostituisci(corrente.titolo, mappa)}</h2>
                    {corrente._prestazione && (
                        <p className="modulo-nota">Prestazione: {corrente._prestazione}</p>
                    )}

                    <section className="modulo-sezione">
                        {medico && (
                            <div className="fc-medico">
                                <strong>Dott./Dott.ssa {medico.cognome} {medico.nome}</strong>
                                <span>
                                    {[medico.specializzazione,
                                      medico.numero_ordine ? `Iscr. Ordine n. ${medico.numero_ordine}` : null]
                                        .filter(Boolean).join(' · ') || 'Medico esecutore'}
                                </span>
                            </div>
                        )}
                        <div className="riepilogo-paziente">
                            <div className="riepilogo-nome">{paziente?.cognome} {paziente?.nome}</div>
                            <div className="riepilogo-dettagli">
                                nato/a a {paziente?.luogo_nascita || '—'} il {dataIta(paziente?.data_nascita)}
                                {' — C.F. '}{paziente?.codice_fiscale}
                            </div>
                        </div>
                    </section>

                    <section className="modulo-sezione fc-contenuto">
                        {blocchi.map((b, i) => {
                            if (b.tipo === 'sezione') {
                                return <h3 key={i} className="fc-sezione">{sostituisci(b.testo, mappa)}</h3>;
                            }
                            if (b.tipo === 'paragrafo') {
                                return (
                                    <p key={i} className="fc-paragrafo">
                                        {conFormattazione(sostituisci(b.testo, mappa))}
                                    </p>
                                );
                            }
                            if (b.tipo === 'elenco') {
                                const Elenco = b.ordinato ? 'ol' : 'ul';
                                return (
                                    <Elenco key={i} className="fc-elenco">
                                        {(b.voci || []).map((v, j) => (
                                            <li key={j}>{conFormattazione(sostituisci(v, mappa))}</li>
                                        ))}
                                    </Elenco>
                                );
                            }
                            if (b.tipo === 'consenso') {
                                return (
                                    <div key={i} className="voce-consenso">
                                        <h4>
                                            {sostituisci(b.etichetta, mappa)}
                                            {b.obbligatorio && <span className="fc-obbligatorio"> obbligatorio</span>}
                                        </h4>
                                        {b.descrizione && (
                                            <p className="voce-descrizione">{sostituisci(b.descrizione, mappa)}</p>
                                        )}
                                        <div className="scelta-consenso">
                                            <label className={`scelta ${risposte[i] === true ? 'attiva si' : ''}`}>
                                                <input type="radio" checked={risposte[i] === true}
                                                    onChange={() => { setRisposte((p) => ({ ...p, [i]: true })); setErrore(null); }} />
                                                <span>Acconsento</span>
                                            </label>
                                            <label className={`scelta ${risposte[i] === false ? 'attiva no' : ''}`}>
                                                <input type="radio" checked={risposte[i] === false}
                                                    onChange={() => { setRisposte((p) => ({ ...p, [i]: false })); setErrore(null); }} />
                                                <span>Non acconsento</span>
                                            </label>
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </section>

                    {fea && (
                        <section className="modulo-sezione" data-firma-tavoletta>
                            <h3>Firma del paziente</h3>
                            <div className="fc-firme">
                                <div className="fc-firma">
                                    <span className="fc-firma-etichetta">Firma con la tavoletta</span>
                                    <div className="fc-tavoletta" role="status" aria-live="polite" data-passo={passoTavoletta || 'attesa'}>
                                        {conMedico
                                            ? MSG_FEA_CON_MEDICO
                                            : (passoTavoletta ? FRASI_TAVOLETTA[passoTavoletta] : FRASI_TAVOLETTA.attesa)}
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {!fea && (
                    <section className="modulo-sezione">
                        <h3>{conMedico ? 'Firme' : 'Firma del paziente'}</h3>
                        <div className={conMedico ? 'fc-firme doppia' : 'fc-firme'}>
                            <div className="fc-firma">
                                <span className="fc-firma-etichetta">Firma del paziente</span>
                                <FirmaCanvas
                                    valore={firmaPaziente}
                                    onChange={(v) => { setFirmaPaziente(v); setErrore(null); }}
                                    etichetta={`Santarcangelo di Romagna, ${new Date().toLocaleDateString('it-IT')}`}
                                />
                            </div>
                            {conMedico && (
                                <div className="fc-firma">
                                    <span className="fc-firma-etichetta">Firma del medico</span>
                                    <FirmaCanvas
                                        valore={firmaMedico}
                                        onChange={(v) => { setFirmaMedico(v); setErrore(null); }}
                                        etichetta={medico ? `Dott./Dott.ssa ${medico.cognome}` : 'Medico esecutore'}
                                    />
                                </div>
                            )}
                        </div>
                    </section>
                    )}

                    {errore && <div className="fc-errore">{errore}</div>}
                </div>

                <footer className="modulo-footer">
                    <button className="btn-annulla" onClick={saltaTutto} disabled={invio}>
                        {obbligatori ? 'Annulla' : 'Firma più tardi'}
                    </button>
                    <button className="btn-conferma" onClick={firma} disabled={invio || (fea && conMedico)}>
                        {invio
                            ? (fea && passoTavoletta ? 'Firma in corso…' : 'Archiviazione…')
                            : fea
                                ? (indice < consensi.length - 1 ? 'Firma con la tavoletta e prosegui' : 'Firma con la tavoletta')
                                : indice < consensi.length - 1
                                    ? 'Firma e prosegui'
                                    : 'Firma e archivia'}
                    </button>
                </footer>

            </div>
        </div>
    );
};

export default FirmaConsenso;
